import React, { useMemo, useEffect } from 'react';
import Localize from 'react-intl-universal';

import { addDays, subDays, parseISO, format } from 'date-fns';
import { Field, Form, FieldArray, useFormikContext } from 'formik';
import { get } from 'lodash-es';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { formatDateTimeToMDYSlashDelimiter, dateInitFormats } from '@common/Util';
import CheckboxGroupInputFields from '@components/CheckboxGroupInputFields/CheckboxGroupInputFields';
import DatePicker from '@components/DatePicker';
import MultipleCheckboxInput from '@components/MultipleCheckboxInput/MultipleCheckboxInput';
import RadioGroupField from '@components/RadioGroup/RadioGroupField';
import { useGlobalContext } from '@hooks/Context/GlobalContext';

import FormComponentMap from './FormComponentMap';
import { foodPreferencesOptions, healthNeeds } from './mockedData';
import { customFieldNamePrefix, mapCustomFieldsToFieldsConfig } from './util';

const StyledHeader = styled(Typography)(({ theme }) => ({
  marginBottom: '1.5rem',
  paddingTop: 0,
  fontSize: '1.7rem',
  fontWeight: 700,
  color: theme.palette.booking.header
}));

const StyledGrid = styled(Grid)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: '7px',
  marginBottom: '5px'
}));

const paddingBasedOnScreenSize = {
  paddingLeft: { xs: '5px !important', sm: '16px' },
  paddingRight: { xs: '5px', sm: '16px' }
};

const renderCustomFields = (customFields, participantIndex, setFieldValue) => {
  return (
    <>
      <Grid item xs={12} sm={12} mb={2} sx={{ ...paddingBasedOnScreenSize }}>
        <Typography sx={{ opacity: 0.7 }}>{Localize.get('Booking.additionalInfo')}</Typography>
      </Grid>
      {customFields.map((customField) => {
        const fieldName = `additionalInfo.${participantIndex}.${customFieldNamePrefix}.${customField?.id}`;
        const { component, ...rest } = mapCustomFieldsToFieldsConfig(
          customField,
          setFieldValue,
          fieldName
        );
        return (
          <Grid
            key={customField.id}
            item
            xs={12}
            sm={6}
            sx={{ ...paddingBasedOnScreenSize, mb: 1 }}
          >
            <FormControl fullWidth size='small' sx={{ alignItems: null }}>
              <FormLabel
                component='legend'
                required={rest?.required}
                sx={{
                  mb: 0.75,
                  lineHeight: 1.3,
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere'
                }}
              >
                {rest?.label}
              </FormLabel>
              <Field {...rest} label={undefined} component={FormComponentMap[component]} />
            </FormControl>
          </Grid>
        );
      })}
    </>
  );
};

const CreateFormArray = ({ values, setIsFormValid, setValues, setFieldValue, eventData }) => {
  const { submitForm, isValid } = useFormikContext();
  const [contextData] = useGlobalContext();
  const { allergy: allergiesOptions } = contextData?.bookingFormData || {};
  const customFields = eventData?.customFields;

  useEffect(() => {
    setIsFormValid(isValid);
  }, [isValid]);

  useEffect(() => {
    return () => {
      submitForm();
    };
  }, []);

  const hotelRequestOptions = useMemo(
    () => [
      { value: false, label: Localize.get('App.no') },
      { value: true, label: Localize.get('App.yes') }
    ],
    []
  );

  return (
    <Form>
      <FieldArray name='additionalInfo'>
        {() => (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', padding: 2, gap: 5 }}>
              {values.additionalInfo.length > 0 &&
                values.additionalInfo.map((value, index) => (
                  <StyledGrid key={index} container spacing={2} padding={{ xs: '5px', sm: '16px' }}>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      sx={{ ...paddingBasedOnScreenSize, mb: '1rem', pt: '0px !important' }}
                    >
                      <StyledHeader>{value?.name || ''}</StyledHeader>
                    </Grid>

                    {value.hotelRequest !== undefined && (
                      <>
                        <Grid item xs={12} sm={12} sx={{ ...paddingBasedOnScreenSize, mb: 1 }}>
                          <FormControl size='small' sx={{ alignItems: null }}>
                            <FormLabel component='legend'>
                              {Localize.get('Booking.hotelRequest')}
                            </FormLabel>
                            <Field
                              name={`additionalInfo.${index}.hotelRequest`}
                              component={RadioGroupField}
                              options={hotelRequestOptions}
                              radioGroupSx={{
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'wrap'
                              }}
                              onChange={(e) => {
                                setValues({
                                  ...values,
                                  additionalInfo: values.additionalInfo.map((v, i) =>
                                    i === index
                                      ? {
                                          ...v,
                                          hotelRequest: e.target.value === 'true' ? true : false,
                                          arrivalDate:
                                            e.target.value === 'true'
                                              ? get(values, `additionalInfo.${index}.arrivalDate`)
                                              : format(
                                                  eventData?.startDateTime,
                                                  dateInitFormats.datePickerFormat
                                                ) || null,
                                          departureDate:
                                            e.target.value === 'true'
                                              ? get(values, `additionalInfo.${index}.departureDate`)
                                              : format(
                                                  eventData?.endDateTime,
                                                  dateInitFormats.datePickerFormat
                                                ) || null
                                        }
                                      : v
                                  )
                                });
                              }}
                            />
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} sx={{ ...paddingBasedOnScreenSize, mb: 1 }}>
                          <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                            <Field
                              disabled={!value.hotelRequest}
                              name={`additionalInfo.${index}.arrivalDate`}
                              component={DatePicker}
                              minDate={subDays(eventData?.startDateTime, 1)}
                              maxDate={parseISO(eventData?.endDateTime)}
                              label={Localize.get('Booking.arrivalDate')}
                              required
                              onChange={(value) => {
                                setFieldValue(
                                  `additionalInfo.${index}.arrivalDate`,
                                  formatDateTimeToMDYSlashDelimiter(value)
                                );
                                setFieldValue(
                                  `additionalInfo.${index}.departureDate`,
                                  formatDateTimeToMDYSlashDelimiter(addDays(value, 1))
                                );
                                setTimeout(() => {
                                  document
                                    .getElementsByName(`additionalInfo.${index}.arrivalDate`)?.[0]
                                    .focus();
                                  const nextElement = document.getElementsByName(
                                    `additionalInfo.${index}.departureDate`
                                  )?.[0];
                                  if (nextElement) {
                                    nextElement.focus();
                                  }
                                }, 500);
                              }}
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ ...paddingBasedOnScreenSize, mb: 1 }}>
                          <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                            <Field
                              disabled={!value.hotelRequest}
                              name={`additionalInfo.${index}.departureDate`}
                              component={DatePicker}
                              minDate={parseISO(eventData?.startDateTime)}
                              maxDate={addDays(eventData?.endDateTime, 1)}
                              label={Localize.get('Booking.departureDate')}
                              required
                              onChange={(value) => {
                                setFieldValue(
                                  `additionalInfo.${index}.departureDate`,
                                  formatDateTimeToMDYSlashDelimiter(value)
                                );
                                setTimeout(() => {
                                  document
                                    .getElementsByName(`additionalInfo.${index}.departureDate`)?.[0]
                                    .focus();
                                  const nextElement = document.getElementsByName(
                                    `additionalInfo.${index}.vegan`
                                  )?.[0];
                                  if (nextElement) {
                                    nextElement.focus();
                                  }
                                }, 500);
                              }}
                            />
                          </FormControl>
                        </Grid>
                      </>
                    )}

                    {value.handicapped !== undefined && (
                      <>
                        <Grid item xs={12} sm={6} sx={{ ...paddingBasedOnScreenSize, mb: 1 }}>
                          <CheckboxGroupInputFields
                            options={foodPreferencesOptions}
                            formArrayName={'additionalInfo'}
                            formArrayIndex={index}
                            groupLabel={Localize.get('Booking.foodPreferences')}
                            setValues={setValues}
                            values={values}
                          />
                        </Grid>
                        <Grid item xs={0} sm={6} sx={{ ...paddingBasedOnScreenSize, mb: 1 }} />
                        <Grid item xs={12} sm={6} sx={{ ...paddingBasedOnScreenSize, mb: 1 }}>
                          <CheckboxGroupInputFields
                            options={healthNeeds}
                            formArrayName={'additionalInfo'}
                            formArrayIndex={index}
                            groupLabel={Localize.get('Booking.healthNeeds')}
                            setValues={setValues}
                            values={values}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ ...paddingBasedOnScreenSize, mb: 1 }}>
                          <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                            <FormLabel component='legend'>
                              {Localize.get('Booking.allergies')}
                            </FormLabel>
                            <Field
                              name={`additionalInfo.${index}.allergies`}
                              component={MultipleCheckboxInput}
                              options={allergiesOptions}
                            />
                          </FormControl>
                        </Grid>
                      </>
                    )}

                    {!!customFields?.length &&
                      renderCustomFields(customFields, index, setFieldValue)}
                  </StyledGrid>
                ))}
            </Box>
          </>
        )}
      </FieldArray>
    </Form>
  );
};

export default CreateFormArray;
